import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Skeleton, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddLinkIcon from '@mui/icons-material/AddLink';
import api from '../api/axios';
import { getTemplate } from '../dashboards/templates';

const REFRESH_MS = 60_000;

function RenameDialog({ open, initial, onClose, onSave }) {
  const [name, setName] = useState('');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      TransitionProps={{ onEnter: () => setName(initial || '') }}
    >
      <DialogTitle>Rename dashboard</DialogTitle>
      <DialogContent>
        <TextField value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth size="small" sx={{ mt: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onSave(name.trim()); }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim()} onClick={() => onSave(name.trim())}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DashboardView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dialog, setDialog] = useState(null); // 'source' | 'rename' | 'delete'

  const { data: dashboard, isLoading, error: loadError } = useQuery({
    queryKey: ['dashboard', id],
    queryFn: () => api.get(`/dashboards/${id}`).then((r) => r.data),
  });

  const hasSource = !!dashboard?.source;
  const dataQuery = useQuery({
    queryKey: ['dashboard-data', id, JSON.stringify(dashboard?.source ?? null)],
    queryFn: () => api.get(`/dashboards/${id}/data`).then((r) => r.data),
    enabled: hasSource,
    refetchInterval: REFRESH_MS,
    staleTime: 0,
  });

  const updateMutation = useMutation({
    mutationFn: (data) => api.patch(`/dashboards/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(['dashboard', id], updated);
      qc.invalidateQueries({ queryKey: ['dashboards', updated.projectId] });
      qc.invalidateQueries({ queryKey: ['dashboard-data', id] });
      setDialog(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/dashboards/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', dashboard?.projectId] });
      navigate('/app/dashboard');
    },
  });

  if (isLoading) {
    return <Box><Skeleton variant="rounded" height={320} sx={{ borderRadius: 1.5 }} /></Box>;
  }
  if (loadError || !dashboard) {
    return (
      <Box>
        <Alert severity="error" variant="outlined" action={<Button color="inherit" onClick={() => navigate('/app/dashboard')}>Back</Button>}>
          Dashboard not found.
        </Alert>
      </Box>
    );
  }

  const template = getTemplate(dashboard.template);
  const View = template?.View;
  const fetchedAt = dataQuery.data?.fetchedAt ? new Date(dataQuery.data.fetchedAt) : null;
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => navigate('/app/dashboard')} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h5" lineHeight={1.25} noWrap>{dashboard.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>{template?.name || dashboard.template}</Typography>
            {hasSource && (
              <Tooltip title={template?.sourceHint(dashboard.source) || ''} arrow>
                <Chip size="small" icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                  label={template?.sourceLabel(dashboard.source)} onClick={() => setDialog('source')}
                  sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            )}
            {fetchedAt && (
              <Typography variant="caption">
                updated {fetchedAt.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>
        {hasSource && (
          <Tooltip title="Refresh now">
            <span>
              <IconButton size="small" onClick={() => dataQuery.refetch()} disabled={dataQuery.isFetching}>
                {dataQuery.isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
          <MenuItem onClick={() => { closeMenu(); setDialog('source'); }}>
            <ListItemIcon><AddLinkIcon fontSize="small" /></ListItemIcon>
            {hasSource ? 'Change data source' : (template?.addLabel || 'Add data source')}
          </MenuItem>
          <MenuItem onClick={() => { closeMenu(); setDialog('rename'); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
          </MenuItem>
          <MenuItem onClick={() => { closeMenu(); setDialog('delete'); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
          </MenuItem>
        </Menu>
      </Box>

      {/* Body */}
      {!hasSource ? (
        <Box sx={{
          p: 5, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
        }}>
          <Typography fontWeight={600}>This dashboard has no data yet</Typography>
          <Typography variant="body2" color="text.secondary">
            {template?.emptyText}
          </Typography>
          <Button variant="contained" startIcon={<AddLinkIcon />} onClick={() => setDialog('source')}>
            {template?.addLabel || 'Add data source'}
          </Button>
        </Box>
      ) : dataQuery.isError ? (
        <Alert severity="error" variant="outlined"
          action={<>
            <Button color="inherit" size="small" onClick={() => dataQuery.refetch()}>Retry</Button>
            <Button color="inherit" size="small" onClick={() => setDialog('source')}>Edit source</Button>
          </>}
        >
          Couldn’t load data: {dataQuery.error.response?.data?.error || dataQuery.error.message}
        </Alert>
      ) : !dataQuery.data ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 1.5 }} />
      ) : View ? (
        <View data={dataQuery.data.data} mapping={dashboard.source.mapping} />
      ) : (
        <Alert severity="warning" variant="outlined">Unknown template “{dashboard.template}”.</Alert>
      )}

      {template && (
        <template.SourceDialog open={dialog === 'source'} initial={dashboard.source} onClose={() => setDialog(null)}
          saving={updateMutation.isPending} onSave={(source) => updateMutation.mutate({ source })}
        />
      )}
      <RenameDialog open={dialog === 'rename'} initial={dashboard.name} onClose={() => setDialog(null)}
        onSave={(name) => updateMutation.mutate({ name })}
      />
      <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)}>
        <DialogTitle>Delete “{dashboard.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The dashboard and its source settings will be removed. The source itself is not affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDialog(null)} variant="text" color="inherit">Cancel</Button>
          <Button variant="contained" color="error" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
